<template>
  <div class="panel">
    <div class="content">
      <div class="title-container">
        <div v-if="!editName" class="editable title" @click="editName = true">
          {{ sectionName }}<Pen class="edit-icon" />
        </div>
        <div v-else class="title-edit">
          <FireInput
            ref="sectionName-field"
            class="text-input"
            :value="sectionName"
            @change="value => (sectionName = value)"
          />
          <div class="cancel" @click.stop="undoSectionName">
            <Cross class="icon" />
          </div>
          <div class="validate" @click.stop="changeSectionName">
            <Check class="icon" />
          </div>
        </div>
        <InfoBubbleIcon class="icon info-bubble-anchor" @click="togglePopup" />
        <HelpBubble :content="sectionsInfos" :visible="showPopup" @on-click="closePopup" />
      </div>
      <div class="divider"></div>
      <div class="button-container">
        <div class="check">
          <input
            id="newPage"
            type="checkbox"
            :checked="item.content.startNewPage"
            @change="value => itemChanged(value.target.checked, 'startNewPage')"
          />
          <label for="newPage">{{ $t('editor.newPage') }}</label>
        </div>
        <div class="indentation-space">
          <div
            class="identation-button"
            :class="{ disable: indentationClass === 'none' }"
            @click="
              () => {
                projectIndentation('left');
              }
            "
          >
            <LeftIndentation class="icon" />
          </div>
          <div
            class="identation-button"
            :class="{ disable: indentationClass === 'right-3' }"
            @click="
              () => {
                projectIndentation('right');
              }
            "
          >
            <RightIndentation class="icon" />
          </div>
        </div>
      </div>
      <div class="section">
        <div class="items">
          <div class="item">
            <div class="text">{{ $t('editor.experiences.date') }}</div>
            <div class="field" @click="sectionSelected('date')">
              <FireInput
                ref="date-field"
                class="text-input"
                :value="item.content.date"
                @change="value => itemChanged(value, 'date')"
              />
            </div>
          </div>
          <div class="item">
            <div class="text">{{ $t('editor.experiences.job') }}</div>
            <div class="field" @click="sectionSelected('job')">
              <FireInput
                ref="job-field"
                class="text-input"
                :value="item.content.job"
                @change="value => itemChanged(value, 'job')"
              />
            </div>
          </div>
          <div class="item">
            <div class="text">{{ $t('editor.experiences.client') }}</div>
            <div class="field" @click="sectionSelected('client')">
              <FireInput
                ref="client-field"
                class="text-input"
                :value="item.content.client"
                @change="value => itemChanged(value, 'client')"
              />
            </div>
          </div>
          <div class="item">
            <div class="text relative-container">
              {{ $t('editor.experiences.values') }}

              <MagicWand
                class="magic-wand-container"
                :initial-text="item.content.info"
                :prompt-context="getMagicWandContext()"
                @insert="
                  value => {
                    itemChanged(value, 'info');
                    updateFields();
                  }
                "
              />
            </div>

            <div class="field" @click="sectionSelected('info')">
              <FireArea
                ref="info-field"
                :template-div-container="'info'"
                :value="item.content.info"
                @change="value => itemChanged(value, 'info')"
              />
            </div>
          </div>
          <div class="item">
            <div class="text relative-container">
              {{ $t('editor.experiences.missions') }}

              <MagicWand
                class="magic-wand-container"
                :initial-text="item.content.missions"
                :prompt-context="getMagicWandContext()"
                @insert="
                  value => {
                    itemChanged(value, 'missions');
                    updateFields();
                  }
                "
              />
            </div>

            <div class="field" @click="sectionSelected('missions')">
              <FireArea
                ref="missions-field"
                :template-div-container="'missions'"
                :value="item.content.missions"
                @change="value => itemChanged(value, 'missions')"
              />
            </div>
          </div>
          <div class="item">
            <div class="text relative-container">
              {{ $t('editor.experiences.technos') }}

              <MagicWand
                class="magic-wand-container"
                :initial-text="item.content.technos"
                :prompt-context="getMagicWandContext()"
                @insert="
                  value => {
                    itemChanged(value, 'technos');
                    updateFields();
                  }
                "
              />
            </div>
            <div class="field" @click="sectionSelected('technos')">
              <FireArea
                ref="technos-field"
                :template-div-container="'technos'"
                :value="item.content.technos"
                @change="value => itemChanged(value, 'technos')"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="spacer"></div>
      <div class="secondary-button inverted" @click.stop="removeItem">
        <BinIcon class="icon" />{{ $t('editor.experiences.delete') }}
      </div>
      <div class="secondary-button mt" @click="addItem"><AddIcon class="icon" />{{ $t('editor.experiences.add') }}</div>
    </div>
    <div ref="resize-handle" class="resize">
      <div class="line" />
      <div class="line" />
      <div class="line" />
    </div>
  </div>
</template>

<script>
import panelMixin from './mixins/PanelMixin';
import FireInput from '@/components/inputs/FireInput';
import FireArea from '@/components/inputs/FireArea';
import AddIcon from '@/assets/listing/add.svg';
import BinIcon from '@/assets/listing/bin.svg';
import RightIndentation from '@/assets/icons/right-indentation.svg';
import LeftIndentation from '@/assets/icons/left-indentation.svg';
import sectionInfosData from '@/helpers/SectionHelp.js';
import HelpBubble from '@/components/HelpBubble.vue';
import InfoBubbleIcon from '@/assets/icons/infobulle.svg';
import InfoBubbleMixin from '@/components/panels/mixins/InfoBubbleMixin.vue';
import MagicWand from '@/components/magic-wand/MagicWand.vue';

export default {
  name: 'ExperiencePanel',
  components: {
    InfoBubbleIcon,
    HelpBubble,
    FireInput,
    FireArea,
    AddIcon,
    BinIcon,
    RightIndentation,
    LeftIndentation,
    MagicWand,
  },
  mixins: [panelMixin, InfoBubbleMixin],
  props: {
    subItems: {
      type: Array,
      default: () => [],
    },
    currentSection: {
      type: String,
      default: '',
    },
  },

  emits: ['deleteItem', 'addItem', 'sectionSelected'],

  data() {
    return {
      missions: this.item.content.missions,
      openedProject: [],
      sectionId: 'experiences',
      indentationClass: 'none',
    };
  },
  computed: {
    projects() {
      return this.subItems.filter(i => i.type === 'experiences-projects').sort((a, b) => b.order - a.order);
    },
    sectionsInfos() {
      return sectionInfosData[this.sectionId];
    },
  },
  watch: {
    currentSection(section) {
      if (section && this.$refs[section + '-field']) {
        this.$refs[section + '-field'].focus();
      }
    },
  },
  async mounted() {
    this.checkIfMustOpenPopup(this.sectionId);

    if (this.currentSection && this.$refs[this.currentSection + '-field']) {
      this.$refs[this.currentSection + '-field'].focus();
    }

    if (this.item) {
      if (this.item.content.indentation) {
        this.indentationClass = this.item.content.indentation;
      } else {
        await this.itemChanged(this.indentationClass, 'indentation');
      }
    }
  },
  methods: {
    sectionSelected(section) {
      this.$emit('sectionSelected', section);
    },
    removeItem() {
      this.$emit('deleteItem', this.item);
    },
    addItem() {
      this.$emit('addItem', this.item.type);
    },
    updateFields() {
      this.dontSendNext = true;
      this.$refs['date-field'].input = this.item.content.date;
      this.dontSendNext = true;
      this.$refs['job-field'].input = this.item.content.job;
      this.dontSendNext = true;
      this.$refs['client-field'].input = this.item.content.client;
      this.dontSendNext = true;
      this.$refs['info-field'].input = this.item.content.info;
      this.dontSendNext = true;
      this.$refs['missions-field'].input = this.item.content.missions;
      this.dontSendNext = true;
      this.$refs['technos-field'].input = this.item.content.technos;
    },
    async projectIndentation(direction) {
      if (direction === 'right') {
        if (this.indentationClass === 'none') this.indentationClass = 'right';
        else if (this.indentationClass === 'right') {
          this.indentationClass = 'right-2';
        } else this.indentationClass = 'right-3';
      } else {
        if (this.indentationClass === 'right') this.indentationClass = 'none';
        else if (this.indentationClass === 'right-2') this.indentationClass = 'right';
        else this.indentationClass = 'right-2';
      }
      this.itemChanged(this.indentationClass, 'indentation');
    },

    getMagicWandContext() {
      return JSON.stringify({
        candidate: this.resume.candidate,
        item: this.item,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/inspector.scss';
@use '@/styles/utils.scss';

.relative-container {
  position: relative;
}

.magic-wand-container {
  position: absolute;
  top: 0;
  right: 0;
}

.button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 12px;

  .check {
    display: flex;
    align-items: center;
    margin: 1rem 0;
    overflow: hidden;
    label {
      margin-left: 4px;
      user-select: none;
      display: block;
      @extend .uppercase-first;
      color: colors.$dark-grey-color;
      cursor: pointer;
    }
  }
  .indentation-space {
    display: flex;
    .identation-button {
      gap: 9px;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 31px;
      width: 31px;
      background-color: colors.$white-color;
      border: 1px solid colors.$light-blue-color;
      color: colors.$light-blue-color;
      border-radius: 5px;
      cursor: pointer;

      &:first-child {
        margin-right: 9px;
      }

      &.disable {
        opacity: 0.5;
        pointer-events: none;
        cursor: default;
      }
    }
  }
}
.section {
  .item {
    position: relative;
  }
  &.projects {
    width: 100%;
    border-top: solid 1px rgba(154, 154, 154, 0.5);
    .item {
      .header {
        background: white;
        .text {
          height: 40px;
          line-height: 40px;
          font-size: 16px;
          border-bottom: solid 1px rgba(154, 154, 154, 0.5);
          padding-left: 12px;
          @extend .uppercase-first;
        }
        .add-button {
          height: 40px;
          text-transform: uppercase;
          user-select: none;
          color: colors.$light-blue-color;
          font-weight: bold;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 11px;
          position: absolute;
          bottom: 0;
          top: 0;
          right: 8px;
          .icon {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: white;
            color: colors.$light-blue-color;
            display: flex;
            align-items: center;
            justify-content: center;
            &:not(.selected) {
              background: rgba(235, 234, 234, 0.76);
            }
            &:hover {
              cursor: pointer;
              color: white;
              background: colors.$light-blue-color;
            }
          }
        }
      }
      .project {
        .title-container {
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          position: relative;
          padding-left: 36px;
          &.header {
            height: 40px;
            border-bottom: solid 1px rgba(154, 154, 154, 0.5);
            background-color: transparent;
            .text {
              border: none;
            }
            &:hover {
              opacity: 0.7;
              cursor: pointer;
            }
          }
          .icon {
            width: 24px;
            height: 24px;
            position: absolute;
            left: 8px;
            top: 8px;
            bottom: 8px;
            transform: rotate(0deg);
            transition: all 0.2s;
            &.active {
              transform: rotate(90deg);
            }
            &.remove {
              display: flex;
              align-items: center;
              justify-content: center;
              left: inherit;
              right: 8px;
              top: 10px;
              bottom: 10px;
              color: rgba(0, 0, 0, 0.384);
              font-size: 18px;
              &:hover {
                color: colors.$blue-color;
              }
            }
          }
        }
        &.active {
          .title-container {
            &.header {
              background: rgba(255, 255, 255, 0.411);
              color: colors.$blue-color;
            }
          }
        }
        .dropdown {
          position: relative;
          height: 0;
          overflow: hidden;
          padding: 0;
          opacity: 0;
          transition:
            all 0.3s,
            opacity 0.3s;
          &.active {
            height: 280px;
            padding-bottom: 12px;
            border-bottom: solid 1px rgba(154, 154, 154, 0.5);
            opacity: 1;
            .section {
              height: fit-content;
              .title-container {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                color: #686868;
                margin-top: 6px;
                margin-bottom: 6px;
                margin-left: 10%;
                padding: 0;
              }
              .field {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 80%;
                margin-left: 10%;
                .text-input,
                .fireArea {
                  width: 100%;
                }
              }
              &:nth-child(2n) {
                .title-container {
                  margin-bottom: 0;
                }
              }
            }
          }
        }
      }
    }
  }
}
.spacer {
  width: 100%;
  margin: 16px 0;
}

.title-container {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .icon {
    cursor: pointer;
    height: 24px;
    margin-right: 8px;
    transition-duration: 0.1s;

    &:hover {
      scale: 1.1;
    }

    &:active {
      scale: 0.9;
    }
  }
}
</style>
